import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import { CATEGORIES, FAMILY_CATEGORIES, MONTHS, ExpenseCategory, FamilyCategory } from '../types';
import { 
  FileText, 
  Trash2, 
  Download,
  Search,
  Filter,
  Eye,
  X,
  Loader2,
  Share2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Pencil,
  Save
} from 'lucide-react';
import { fetchDocumentBlob } from '../services/api';

type CategoryFilterValue = ExpenseCategory | FamilyCategory | 'all';

export default function Expenses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { expenses, isLoading, deleteExpense, updateExpense } = useExpenses();
  const { isCPA } = useAuth();
  const { mode, isFamily } = useMode();

  const visibleCategories = isFamily ? FAMILY_CATEGORIES : CATEGORIES;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilterValue>(
    (searchParams.get('category') as CategoryFilterValue) || 'all'
  );

  useEffect(() => {
    if (isFamily && selectedCategory !== 'all' && !FAMILY_CATEGORIES.some(c => c.value === selectedCategory)) {
      setSelectedCategory('all');
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('category');
        return next;
      });
    }
  }, [isFamily, selectedCategory, setSearchParams]);

  const [selectedSource, setSelectedSource] = useState<'all' | 'manual' | 'gmail' | 'paste' | 'import'>('all');
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>('all');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [viewingExpense, setViewingExpense] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  
  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    date: '',
    amount: '',
    description: '',
    category: '' as ExpenseCategory | FamilyCategory
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Bulk delete state
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0 });
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState({ current: 0, total: 0 });

  // Get years from 2020 to current year
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const allYears: number[] = [];
    for (let year = currentYear; year >= 2020; year--) {
      allYears.push(year);
    }
    return allYears;
  }, []);

  // Filter expenses (context already loads by mode; this is a safety filter by expense.mode)
  const filteredExpenses = useMemo(() => {
    const byMode = expenses.filter(e => {
      const expMode = e.mode ?? 'business';
      return mode === 'family' ? expMode === 'family' : expMode !== 'family';
    });
    return byMode.filter(expense => {
      if (selectedCategory !== 'all' && expense.category !== selectedCategory) return false;
      if (selectedSource !== 'all') {
        const src = expense.provenanceSource ?? 'manual';
        if (src !== selectedSource) return false;
      }
      if (selectedMonth !== 'all' && expense.month !== selectedMonth) return false;
      if (expense.year !== selectedYear) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          expense.filename.toLowerCase().includes(search) ||
          expense.description?.toLowerCase().includes(search) ||
          expense.freightBillNumber?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [expenses, mode, selectedCategory, selectedSource, selectedMonth, selectedYear, searchTerm]);

  const getCategoryInfo = (value: string) =>
    CATEGORIES.find(c => c.value === value) ?? FAMILY_CATEGORIES.find(c => c.value === value);

  const getLocationLabel = (expense: typeof expenses[0]) => {
    const route = [expense.origin, expense.destination].filter(Boolean).join(' → ');
    if (route) return route;

    const vendorParts: string[] = [];
    if (expense.vendor) vendorParts.push(expense.vendor);
    if (expense.storeNumber) vendorParts.push(`Store ${expense.storeNumber}`);
    if (vendorParts.length) return vendorParts.join(' • ');

    return '';
  };

  const handleCategoryChange = (cat: CategoryFilterValue) => {
    setSelectedCategory(cat);
    if (cat === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', cat);
    }
    setSearchParams(searchParams);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      setDeletingId(id);
      try {
        await deleteExpense(id);
      } finally {
        setDeletingId(null);
      }
    }
  };

  // Bulk delete all filtered expenses
  const handleBulkDelete = async () => {
    const count = filteredExpenses.length;
    const categoryLabel = selectedCategory === 'all' ? 'all categories' : selectedCategory;
    const monthLabel = selectedMonth === 'all' ? 'all months' : MONTHS.find(m => m.value === selectedMonth)?.label;
    
    const message = `Are you sure you want to delete ${count} expense(s)?\n\nFilters: ${categoryLabel}, ${monthLabel}, ${selectedYear}\n\nThis cannot be undone!`;
    
    if (window.confirm(message)) {
      setIsBulkDeleting(true);
      setBulkDeleteProgress({ current: 0, total: count });
      
      try {
        for (let i = 0; i < filteredExpenses.length; i++) {
          await deleteExpense(filteredExpenses[i].id);
          setBulkDeleteProgress({ current: i + 1, total: count });
        }
      } catch (err) {
        console.error('Bulk delete error:', err);
        alert('Some expenses failed to delete. Please try again.');
      } finally {
        setIsBulkDeleting(false);
        setBulkDeleteProgress({ current: 0, total: 0 });
      }
    }
  };

  // Convert base64 to blob
  const base64ToBlob = (base64: string): Blob => {
    const parts = base64.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const getTextFromDataUrl = (dataUrl: string): string | null => {
    try {
      if (!dataUrl.startsWith('data:')) return null;
      const commaIndex = dataUrl.indexOf(',');
      if (commaIndex === -1) return null;
      const meta = dataUrl.slice(0, commaIndex);
      const data = dataUrl.slice(commaIndex + 1);
      if (meta.toLowerCase().includes(';base64')) {
        return atob(data);
      }
      return decodeURIComponent(data);
    } catch {
      return null;
    }
  };

  const handleDownload = async (expense: typeof expenses[0]) => {
    setIsDownloading(true);
    try {
      const blob = base64ToBlob(expense.fileData);
      
      // Try Web Share API first (works well on mobile)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], expense.filename, { type: expense.fileType });
        const shareData = { files: [file] };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          setIsDownloading(false);
          return;
        }
      }
      
      // Fallback: Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = expense.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Download failed:', err);
      // Last resort: open in new tab
      window.open(expense.fileData, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  const extractRouteFromText = (text: string): { origin?: string; destination?: string } => {
    const clean = (value: string) =>
      value
        .replace(/\s+/g, ' ')
        .replace(/^[•\-\s]+|[•\-\s]+$/g, '')
        .trim();

    let origin = '';
    let destination = '';

    const pickupMatch = text.match(/(?:Pickup|Pick-up|Start)\s*:?\s*([^\n]+?)(?:\n|$)/i);
    const dropoffMatch = text.match(/(?:Dropoff|Drop-off|Drop Off|Destination)\s*:?\s*([^\n]+?)(?:\n|$)/i);
    if (pickupMatch?.[1]) origin = clean(pickupMatch[1]);
    if (dropoffMatch?.[1]) destination = clean(dropoffMatch[1]);

    if (!origin || !destination) {
      const fromToMatch = text.match(/From\s+([^\n]+?)\s+to\s+([^\n]+)/i);
      if (fromToMatch) {
        if (!origin) origin = clean(fromToMatch[1]);
        if (!destination) destination = clean(fromToMatch[2]);
      }
    }

    if (!destination) {
      const toMatch = text.match(/\bto\s+([^\n()]+?)(?:\(|$)/i);
      if (toMatch?.[1]) destination = clean(toMatch[1]);
    }

    return { origin, destination };
  };

  const handleShare = async (expense: typeof expenses[0]) => {
    try {
      const blob = base64ToBlob(expense.fileData);
      const file = new File([blob], expense.filename, { type: expense.fileType });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: expense.filename,
          text: expense.description || `${expense.category} expense`
        });
      } else {
        // Fallback to download
        await handleDownload(expense);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  const handleBackfillTravelLocations = async () => {
    if (!window.confirm('Backfill travel locations from existing text? This will update travel entries missing location/vendor.')) {
      return;
    }

    const candidates = expenses.filter(
      (expense) =>
        expense.category === 'travel' &&
        (!expense.origin || !expense.destination || !expense.vendor)
    );

    if (candidates.length === 0) {
      alert('No travel entries found that need backfill.');
      return;
    }

    setIsBackfilling(true);
    setBackfillProgress({ current: 0, total: candidates.length });

    try {
      for (let i = 0; i < candidates.length; i++) {
        const expense = candidates[i];
        const text = getTextFromDataUrl(expense.fileData) || '';
        const combinedText = [text, expense.description, expense.filename].filter(Boolean).join('\n');
        const { origin, destination } = extractRouteFromText(combinedText);

        let vendor = expense.vendor;
        const descLower = (expense.description || '').toLowerCase();
        if (!vendor) {
          if (descLower.includes('uber')) vendor = 'Uber';
          else if (descLower.includes('lyft')) vendor = 'Lyft';
        }

        const updatedExpense = {
          ...expense,
          origin: expense.origin || origin,
          destination: expense.destination || destination,
          vendor
        };

        if (
          updatedExpense.origin !== expense.origin ||
          updatedExpense.destination !== expense.destination ||
          updatedExpense.vendor !== expense.vendor
        ) {
          await updateExpense(updatedExpense);
        }

        setBackfillProgress({ current: i + 1, total: candidates.length });
      }
    } catch (err) {
      console.error('Backfill error:', err);
      alert('Backfill failed. Please try again.');
    } finally {
      setIsBackfilling(false);
      setBackfillProgress({ current: 0, total: 0 });
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.5, 0.5));
  const handleResetZoom = () => setZoom(1);

  // Edit handlers
  const startEditing = (expense: typeof expenses[0]) => {
    setEditingId(expense.id);
    
    // Ensure date is in YYYY-MM-DD format for the date input
    let formattedDate = expense.date;
    if (expense.date && !expense.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const d = new Date(expense.date);
      formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    
    setEditForm({
      date: formattedDate,
      amount: expense.amount?.toString() || '',
      description: expense.description || '',
      category: expense.category as ExpenseCategory
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ date: '', amount: '', description: '', category: '' as ExpenseCategory });
  };

  const saveEdit = async (expense: typeof expenses[0]) => {
    // Confirm before saving
    if (!window.confirm('Save these changes?')) {
      return;
    }
    
    setIsSaving(true);
    try {
      // Parse date - handle both YYYY-MM-DD and other formats
      let year: number, month: number, day: number;
      if (editForm.date.includes('-')) {
        [year, month, day] = editForm.date.split('-').map(Number);
      } else if (editForm.date.includes('/')) {
        [month, day, year] = editForm.date.split('/').map(Number);
      } else {
        const d = new Date(editForm.date);
        year = d.getFullYear();
        month = d.getMonth() + 1;
        day = d.getDate();
      }
      
      // Ensure date is in YYYY-MM-DD format for storage
      const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      const updatedExpense = {
        ...expense,
        date: formattedDate,
        amount: editForm.amount ? parseFloat(editForm.amount) : undefined,
        description: editForm.description,
        category: editForm.category,
        month: month,
        year: year
      };
      await updateExpense(updatedExpense);
      setEditingId(null);
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const closeModal = () => {
    setViewingExpense(null);
    setZoom(1);
  };

  const viewingExpenseData = viewingExpense 
    ? expenses.find(e => e.id === viewingExpense) 
    : null;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function loadPdfPreview() {
      if (!viewingExpenseData || viewingExpenseData.fileType !== 'application/pdf') {
        setPdfPreviewUrl(null);
        setPdfPreviewLoading(false);
        return;
      }

      setPdfPreviewLoading(true);

      try {
        const anyExpense = viewingExpenseData as any;
        if (anyExpense.documentId) {
          const blob = await fetchDocumentBlob(anyExpense.documentId as string);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setPdfPreviewUrl(objectUrl);
        } else if (viewingExpenseData.fileData) {
          setPdfPreviewUrl(viewingExpenseData.fileData);
        } else {
          setPdfPreviewUrl(null);
        }
      } catch {
        if (!cancelled) {
          setPdfPreviewUrl(viewingExpenseData?.fileData || null);
        }
      } finally {
        if (!cancelled) {
          setPdfPreviewLoading(false);
        }
      }
    }

    loadPdfPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [viewingExpenseData?.id, viewingExpenseData?.fileType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Expenses</h1>
          <p className="text-white/60 mt-1">
            {filteredExpenses.length} document{filteredExpenses.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by filename, description..."
            className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
          />
        </div>

        {/* Filter Row */}
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-5 h-5 text-white/60" />
          
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value as CategoryFilterValue)}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            <option value="all">All Categories</option>
            {visibleCategories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          {/* Source Filter */}
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value as typeof selectedSource)}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            <option value="all">All Sources</option>
            <option value="gmail">Gmail</option>
            <option value="manual">Manual</option>
            <option value="import">Import</option>
            <option value="paste">Paste</option>
          </select>

          {/* Month Filter */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            <option value="all">All Months</option>
            {MONTHS.map((month, i) => (
              <option key={i} value={i + 1}>{month}</option>
            ))}
          </select>

          {/* Year Filter */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          </div>
          
          {/* Bulk Delete Button - only show if not CPA and has filtered expenses */}
          {!isCPA && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleBackfillTravelLocations}
                disabled={isBackfilling}
                className="px-4 py-2 bg-amber-500/20 text-amber-300 rounded-lg hover:bg-amber-500/30 disabled:opacity-50 transition-colors flex items-center gap-2 whitespace-nowrap"
                title="Backfill travel locations from existing text"
              >
                {isBackfilling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Backfilling {backfillProgress.current}/{backfillProgress.total}...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Backfill Travel Locations
                  </>
                )}
              </button>
              {filteredExpenses.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 transition-colors flex items-center gap-2 whitespace-nowrap"
                  title={`Delete all ${filteredExpenses.length} filtered expenses`}
                >
                  {isBulkDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting {bulkDeleteProgress.current}/{bulkDeleteProgress.total}...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete All ({filteredExpenses.length})
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {filteredExpenses.length > 0 ? (
          <div className="divide-y divide-white/10">
            {filteredExpenses.map((expense) => {
              const category = getCategoryInfo(expense.category);
              const isEditing = editingId === expense.id;
              
              return (
                <div
                  key={expense.id}
                  className={`p-4 hover:bg-white/5 transition-colors ${isEditing ? 'bg-white/5' : ''}`}
                >
                  {isEditing ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{category?.icon || '📄'}</span>
                        <span className="text-white font-medium truncate">{expense.filename}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-white/60 text-xs">Date (YYYY-MM-DD)</label>
                          <input
                            type="text"
                            value={editForm.date}
                            onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                            placeholder="2025-12-12"
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-white/60 text-xs">Amount</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.amount}
                            onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-white/60 text-xs">Description</label>
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="Description"
                        />
                      </div>
                      <div>
                        <label className="text-white/60 text-xs">Category</label>
                        <select
                          value={editForm.category}
                          onChange={(e) => setEditForm({...editForm, category: e.target.value as ExpenseCategory | FamilyCategory})}
                          className="w-full px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white text-sm"
                        >
                          {visibleCategories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          onClick={cancelEditing}
                          className="px-4 py-2 text-white/60 hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => saveEdit(expense)}
                          disabled={isSaving}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                        >
                          {isSaving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 min-w-0">
                        <span className="text-2xl flex-shrink-0">{category?.icon || '📄'}</span>
                        <div className="min-w-0">
                          <p className="font-medium text-white truncate">
                            {expense.filename}
                          </p>
                          <p className="text-sm text-white/60 truncate">
                            {[expense.description || category?.label, getLocationLabel(expense), new Date(expense.date).toLocaleDateString()]
                              .filter(Boolean)
                              .join(' • ')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {expense.amount && (
                          <span className="text-white font-medium hidden sm:block">
                            ${expense.amount.toFixed(2)}
                          </span>
                        )}
                        <span className={`category-badge category-${expense.category} hidden sm:inline-flex`}>
                          {category?.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded hidden sm:inline-flex ${
                          (expense.provenanceSource ?? 'manual') === 'gmail' ? 'bg-blue-500/30 text-blue-200' :
                          (expense.provenanceSource ?? 'manual') === 'paste' ? 'bg-amber-500/30 text-amber-200' :
                          (expense.provenanceSource ?? 'manual') === 'import' ? 'bg-emerald-500/30 text-emerald-200' :
                          'bg-white/20 text-white/80'
                        }`}>
                          {(expense.provenanceSource ?? 'manual').charAt(0).toUpperCase() + (expense.provenanceSource ?? 'manual').slice(1)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded hidden sm:inline-flex ${
                          expense.parseStatus === 'parse_error' ? 'bg-red-500/30 text-red-200' :
                          expense.parseStatus === 'saved_only' ? 'bg-yellow-500/30 text-yellow-200' :
                          'bg-green-500/20 text-green-200'
                        }`}>
                          {expense.parseStatus === 'parse_error' ? 'Error' : expense.parseStatus === 'saved_only' ? 'Saved' : 'Parsed'}
                        </span>
                        {expense.fileBacked !== false ? (
                          <span className="text-xs px-2 py-0.5 rounded hidden sm:inline-flex bg-emerald-500/20 text-emerald-200" title="PDF available in R2">
                            ✓ File stored
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded hidden sm:inline-flex bg-amber-500/30 text-amber-200" title="No PDF in storage – legacy placeholder">
                            ⚠ Legacy placeholder (no file)
                          </span>
                        )}
                        {(expense.provenanceSource ?? 'manual') === 'gmail' && expense.parseStatus === 'saved_only' && (
                          <span className="text-xs px-2 py-0.5 rounded hidden sm:inline-flex bg-white/10 text-white/70" title="Attachment stored from Gmail but not parsed">
                            Attachment saved – not parsed
                          </span>
                        )}
                        {/* Edit button - owner only */}
                        {!isCPA && (
                          <button
                            onClick={() => startEditing(expense)}
                            className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4 text-blue-400" />
                          </button>
                        )}
                        <button
                          onClick={() => expense.fileBacked !== false && setViewingExpense(expense.id)}
                          disabled={expense.fileBacked === false}
                          className={`p-2 rounded-lg transition-colors ${expense.fileBacked === false ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'}`}
                          title={expense.fileBacked === false ? 'No file available' : 'View'}
                        >
                          <Eye className="w-4 h-4 text-white/60" />
                        </button>
                        <button
                          onClick={() => expense.fileBacked !== false && handleShare(expense)}
                          disabled={expense.fileBacked === false}
                          className={`p-2 rounded-lg transition-colors ${expense.fileBacked === false ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'}`}
                          title={expense.fileBacked === false ? 'No file available' : 'Share/Download'}
                        >
                          <Share2 className="w-4 h-4 text-white/60" />
                        </button>
                        {/* Delete button - owner only */}
                        {!isCPA && (
                          <button
                            onClick={() => handleDelete(expense.id)}
                            disabled={deletingId === expense.id}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Delete"
                          >
                            {deletingId === expense.id ? (
                              <Loader2 className="w-4 h-4 text-red-400 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-red-400" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center text-white/60">
            <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
            <p>No expenses found</p>
            <p className="text-sm text-white/40 mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* View Modal with Zoom */}
      {viewingExpenseData && (
        <div 
          className="fixed inset-0 bg-black/90 flex flex-col z-50"
          onClick={closeModal}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 bg-shuttle-dark/90 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white truncate flex-1 mr-4">
              {viewingExpenseData.filename}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleShare(viewingExpenseData)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Share to Google Drive / Files"
              >
                <Share2 className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => handleDownload(viewingExpenseData)}
                disabled={isDownloading}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Download"
              >
                {isDownloading ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Download className="w-5 h-5 text-white" />
                )}
              </button>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Zoom Controls */}
          {viewingExpenseData.fileType.startsWith('image/') && (
            <div 
              className="flex items-center justify-center space-x-4 py-2 bg-black/50"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <ZoomOut className="w-5 h-5 text-white" />
              </button>
              <span className="text-white text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 4}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <ZoomIn className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={handleResetZoom}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <RotateCcw className="w-4 h-4 text-white" />
              </button>
            </div>
          )}

          {/* Image/Content Area - Scrollable and Zoomable */}
          <div 
            ref={imageContainerRef}
            className="flex-1 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-h-full flex items-center justify-center p-4">
              {viewingExpenseData.fileType.startsWith('image/') ? (
                <img
                  src={viewingExpenseData.fileData}
                  alt={viewingExpenseData.filename}
                  className="max-w-none transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom})`,
                    transformOrigin: 'center center'
                  }}
                  draggable={false}
                />
              ) : viewingExpenseData.fileType === 'application/pdf' ? (
                pdfPreviewUrl ? (
                  <iframe
                    src={pdfPreviewUrl}
                    title={viewingExpenseData.filename}
                    className="w-full h-[80vh] rounded-lg bg-white"
                  />
                ) : (
                  <div className="w-full h-[80vh] flex items-center justify-center bg-white">
                    {pdfPreviewLoading ? (
                      <span className="text-shuttle-dark text-sm">Loading PDF…</span>
                    ) : (
                      <span className="text-shuttle-dark text-sm">Preview not available</span>
                    )}
                  </div>
                )
              ) : (
                <div className="text-center py-8 text-white/60">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-white/30" />
                  <p>Preview not available</p>
                  <button
                    onClick={() => handleDownload(viewingExpenseData)}
                    className="mt-4 px-4 py-2 bg-shuttle-sand text-shuttle-dark rounded-lg font-medium hover:bg-amber-400 transition-colors"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer with details */}
          <div 
            className="p-4 bg-shuttle-dark/90 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-white/50">Category</p>
                <p className="text-white font-medium">
                  {getCategoryInfo(viewingExpenseData.category)?.label}
                </p>
              </div>
              <div className="text-center">
                <p className="text-white/50">Date</p>
                <p className="text-white font-medium">
                  {new Date(viewingExpenseData.date).toLocaleDateString()}
                </p>
              </div>
              {getLocationLabel(viewingExpenseData) && (
                <div className="text-center">
                  <p className="text-white/50">Location</p>
                  <p className="text-white font-medium">
                    {getLocationLabel(viewingExpenseData)}
                  </p>
                </div>
              )}
              {viewingExpenseData.amount && (
                <div className="text-center">
                  <p className="text-white/50">Amount</p>
                  <p className="text-white font-medium">${viewingExpenseData.amount.toFixed(2)}</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-white/50">Size</p>
                <p className="text-white font-medium">
                  {(viewingExpenseData.fileSize / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
